import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { Plus, Save, ClipboardList, User, XCircle } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import { toLocalDateInput } from '../../utils/formatters'; // Assuming this is needed for dates

interface QuoteForm {
  clientId: string;
  title: string;
  description: string;
  items: Array<{
    itemType: 'service' | 'product';
    description: string;
    quantity: number;
    unitPrice: number;
    // ➜ pour l'édition + sélecteurs
    serviceTypeId?: string;
    productId?: string;
    equipmentId?: string;
    taxRate?: number;
  }>;
  taxRate: number;
  notes?: string;
  requestId?: string;
}

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  company ? : string;
}

interface Request {
  _id: string;
  title: string;
  description: string;
  clientId: { _id: string; firstName: string; lastName: string; company ? : string };
  serviceTypeId: { _id: string; name: string; category: string };
  notes ? : string;
}

// Normalise une quote retournée par l'API (avec populate) vers la forme attendue par le formulaire
function normalizeQuoteFromApi(raw: any): QuoteForm {
  const toId = (v: any) => (v && typeof v === 'object' && v._id ? v._id : v || '');

  return {
    clientId: toId(raw.clientId),
    title: raw.title || '',
    description: raw.description || '',
    taxRate: Number(raw.taxRate ?? 0),
    notes: raw.notes || '',
    requestId: raw.requestId ? String(toId(raw.requestId)) : undefined,
    items: (raw.items || []).map((it: any) => {
      const itemType = it.itemType === 'product' ? 'product' : 'service';
      const desc =
        it.description ??
        (itemType === 'product' ? it.productId?.name : it.serviceTypeId?.name) ?? '';
      const unit =
        typeof it.unitPrice === 'number'
          ? it.unitPrice
          : (itemType === 'product' ? Number(it.productId?.price ?? 0) : Number(it.unitPrice ?? 0));

      return {
        itemType,
        description: String(desc || ''),
        quantity: Number(it.quantity ?? 1),
        unitPrice: Number(unit ?? 0),
        // ➜ garder les IDs pour que les selects se remplissent en édition
        productId: toId(it.productId),
        serviceTypeId: toId(it.serviceTypeId),
        equipmentId: toId(it.equipmentId),
        taxRate: Number(raw.taxRate ?? it.taxRate ?? 0),
      };
    }),
  };
}

const AddQuote: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState < Client[] > ([]);

  // ✦ Types catalogue (adapte si ton API renvoie d'autres noms de champs)
  type ServiceType = { _id: string; name: string; price?: number; basePrice?: number; defaultPrice?: number; };
  type Product    = { _id: string; name: string; price?: number; salePrice?: number; sku?: string; };
  type KitItem    = { productId: string | { _id: string; name: string; price?: number; salePrice?: number; }; quantity: number; unitPrice?: number; };
  type Kit        = { _id: string; name: string; items: KitItem[]; price?: number };

  // ✦ Dans ton composant AddQuote …
  const [services, setServices] = useState<ServiceType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [kits, setKits]         = useState<Kit[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  // Util : format prix EUR (adapte la monnaie si besoin)
  const formatPrice = (n: number | undefined) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

  // Trouver le bon prix selon le type de donnée renvoyée par l’API
  const svcPrice = (s: ServiceType) => s.price ?? s.basePrice ?? s.defaultPrice ?? 0;
  const prodPrice = (p: Product) => p.salePrice ?? p.price ?? 0;

  // Charge les catalogues (utilise apiService.* qui existe déjà chez toi)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingCatalog(true);
        const [svcRes, prodRes, kitRes] = await Promise.all([
          apiService.serviceTypes.getAll?.(),
          apiService.products.getAll?.(),
          apiService.kits?.getAll?.(),
        ]);

        const toArr = (r: any, preferKeys: string[] = []) => {
          // r peut être un AxiosResponse ou déjà un objet data
          const d = r?.data ?? r;

          // chemins génériques fréquents
          const candidates = [
            // clés prioritaires (si tu sais comment ton backend nomme la collection)
            ...preferKeys.map(k => d?.data?.[k]).filter(Boolean),
            ...preferKeys.map(k => d?.[k]).filter(Boolean),

            // formes génériques type pagination
            d?.data?.items,
            d?.items,
            d?.data?.results,
            d?.results,
            d?.data?.list,
            d?.list,

            // fallback: parfois l'array est directement dans data
            Array.isArray(d?.data) ? d?.data : undefined,
            Array.isArray(d) ? d : undefined,
          ].filter(Boolean);

          const arr = candidates.find(Array.isArray) as any[] | undefined;
          return arr ?? [];
        };

        if (!mounted) return;
        setServices(toArr(svcRes, ['serviceTypes']));
        setProducts(toArr(prodRes, ['products']));
        setKits(toArr(kitRes, ['kits']));
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger le catalogue (services/produits/kits).");
      } finally {
        setLoadingCatalog(false);
      }
    })();
    return () => { mounted = false; };
}, []);

  const { register, handleSubmit, setValue, formState: { errors }, control, reset, watch } = useForm<QuoteForm>();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await apiService.users.getClients();
        const list = response?.items || [];
        setClients(list);
      } catch (error) {
        console.error('Error fetching clients:', error);
        toast.error('Erreur lors du chargement des clients.');
      }
    };

    fetchClients();
  }, []);

  

    const { fields, append, remove, replace, update } = useFieldArray({ control, name: "items" });

  // Pour manipuler une ligne en fonction d'un service / produit
  type ItemType = 'service' | 'product' | 'kit';

  const onSelectService = (index: number, svc: ServiceType) => {
    update(index, {
      ...watch(`items.${index}`),
      itemType: 'service',
      serviceTypeId: svc._id,
      productId: undefined,
      description: svc.name,
      quantity: watch(`items.${index}.quantity`) || 1,
      unitPrice: Number(svcPrice(svc)) || 0,
    });
  };

  const onSelectProduct = (index: number, p: Product) => {
    update(index, {
      ...watch(`items.${index}`),
      itemType: 'product',
      productId: p._id,
      serviceTypeId: undefined,
      description: p.name,
      quantity: watch(`items.${index}.quantity`) || 1,
      unitPrice: Number(prodPrice(p)) || 0,
    });
  };

  // Si "Kit" → on remplace la ligne par les produits du kit (pas d’itemType 'kit' côté API)
  const expandKitIntoItems = (index: number, kit: Kit) => {
    // Retire la ligne vide/squelette
    remove(index);

    const newItems = kit.items.map((ki) => {
      const pid   = typeof ki.productId === 'string' ? ki.productId : ki.productId?._id;
      const pname = typeof ki.productId === 'string'
        ? (products.find(p => p._id === ki.productId)?.name || 'Produit du kit')
        : (ki.productId?.name ?? 'Produit du kit');
      const punit = typeof ki.productId === 'string'
        ? prodPrice(products.find(p => p._id === ki.productId) as Product)
        : (ki.unitPrice ?? prodPrice(ki.productId as any));

      return {
        itemType: 'product' as const,
        productId: pid,
        serviceTypeId: undefined,
        description: `${pname} (Kit: ${kit.name})`,
        quantity: ki.quantity || 1,
        unitPrice: Number(punit) || 0,
        taxRate: watch('taxRate') ?? 0,
      };
    });

    // Insère les lignes du kit
    newItems.forEach((ni, i) => append(ni));
  };

  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  console.log('AddQuote: id =', id, 'isEditMode =', isEditMode);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const fromRequestId = queryParams.get('fromRequest');

    const fetchRequestData = async (requestId: string) => {
      try {
        const response = await apiService.requests.getById(requestId);
        console.log("API: getById raw response data:", response.data);
        const request: Request = response.data.data.request;

        setValue('clientId', request.clientId._id);
        setValue('title', `Devis pour ${request.title}`);
        setValue('description', request.description);
        setValue('notes', request.notes);
        setValue('requestId', request._id);

        // Clear existing items and add the pre-filled one
        replace([
          {
            itemType: 'service',
            description: `Service: ${request.serviceTypeId.name}`,
            quantity: 1,
            unitPrice: 0,
          }
        ]);
      } catch (error) {
        console.error('Error fetching request data:', error);
        toast.error('Erreur lors du chargement des données de la demande.');
      }
    };

    if (fromRequestId) {
      fetchRequestData(fromRequestId);
    }
  }, [location.search, setValue, append, remove]);

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    if (!fields.length && !qp.get('fromRequest')) {
      append({ itemType: 'service', description: '', quantity: 1, unitPrice: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditMode || !id) return;

    const loadQuote = async () => {
      try {
        const res = await apiService.quotes.getById(id);
        // Adapte si ton backend renvoie différemment
        const quote = res.data?.data?.quote ?? res.data?.quote ?? res.data;

        const normalized = normalizeQuoteFromApi(quote);

        // 1) Remplir les champs simples
        reset({
          clientId: normalized.clientId,
          title: normalized.title,
          description: normalized.description,
          taxRate: normalized.taxRate,
          notes: normalized.notes,
          requestId: normalized.requestId,
          items: [], // on remplace via useFieldArray juste après
        });

        // 2) Remplacer les items via useFieldArray (bonne pratique RHF)
        replace(
          normalized.items?.length
            ? normalized.items
            : [{ itemType: 'service', description: '', quantity: 1, unitPrice: 0 }]
        );
      } catch (error) {
        console.error('Error loading quote:', error);
        toast.error('Erreur lors du chargement du devis.');
      }
    };

    loadQuote();
  }, [isEditMode, id, reset, replace]);

  const onSubmit = async (data: QuoteForm) => {
    setIsLoading(true);
    const cleanItem = (it: any) => {
      const out: any = {
        ...it,
        itemType: it.itemType || 'service',
        quantity: Number(it.quantity ?? 1),
        unitPrice: Number(it.unitPrice ?? 0),
      };
      // 🔒 Supprime les IDs vides pour passer optional().isMongoId()
      ['productId', 'serviceTypeId', 'equipmentId'].forEach((k) => {
        if (!out[k]) delete out[k];
      });
      // Supprime les champs string vides inutiles
      Object.keys(out).forEach((k) => {
        if (out[k] === '') delete out[k];
      });
      return out;
    };

    const safeData: any = {
      ...data,
      taxRate: Number(data.taxRate ?? 0),
      items: (data.items || []).map(cleanItem),
    };

    // Ne pas envoyer requestId si vide
    if (!safeData.requestId) delete safeData.requestId;

    try {
      if (!data.clientId) { toast.error('Sélectionne un client.'); return; }
      if (!data.items?.length) { toast.error('Ajoute au moins un article.'); return; }

      for (let i = 0; i < safeData.items.length; i++) {
        const it = safeData.items[i];
        if (!String(it.description || '').trim()) {
          toast.error(`Ligne ${i + 1}: la description est requise`);
          return;
        }
        if (it.quantity < 1) { toast.error(`Ligne ${i + 1}: quantité ≥ 1`); return; }
        if (it.unitPrice < 0) { toast.error(`Ligne ${i + 1}: prix ≥ 0`); return; }
      }

      if (isEditMode && id) {
        await apiService.quotes.update(id, safeData);
        toast.success('Devis mis à jour avec succès !');
      } else {
        await apiService.quotes.create(safeData);
        toast.success('Devis créé avec succès !');
      }

      navigate('/admin/quotes');
    } catch (error: any) {
      console.error('Error creating quote:', error);
      console.log('Payload envoyé:', safeData);
      console.log('Réponse API:', error?.response?.data);
      const msg = error?.response?.data?.message || 'Erreur lors de la création du devis.';
      const errs = error?.response?.data?.errors;
      if (errs?.length) toast.error(`${msg} → ${errs[0]?.msg}`);
      else toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/quotes')}>
              <Plus className="w-5 h-5 mr-2" />
              Retour aux Devis
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ClipboardList className="w-8 h-8 mr-3 text-primary-600" />
                {isEditMode ? 'Modifier Devis' : 'Nouveau Devis'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isEditMode ? 'Mettre à jour le devis existant' : 'Créer un nouveau devis'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Client Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-primary-600" />
              Informations Client
            </h3>
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-2">
                Client *
              </label>
              <select
                id="clientId"
                {...register('clientId', { required: 'Le client est requis' })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.company ? `${client.company} (${client.firstName} ${client.lastName})` : `${client.firstName} ${client.lastName}`}
                  </option>
                ))}
              </select>
              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>
              )}
            </div>
          </Card>

          {/* Quote Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <ClipboardList className="w-5 h-5 mr-2 text-primary-600" />
              Détails du Devis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du Devis *
                </label>
                <input
                  id="title"
                  type="text"
                  {...register('title', { required: 'Le titre est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: Devis pour installation borne de recharge"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 mb-2">
                  Taux de Taxe (%)
                </label>
                <input
                  id="taxRate"
                  type="number"
                  step="0.01"
                  {...register('taxRate', { valueAsNumber: true, min: 0, max: 100 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.taxRate && (
                  <p className="mt-1 text-sm text-red-600">{errors.taxRate.message}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  {...register('description', { required: 'La description est requise' })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Description détaillée du devis..."
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  id="notes"
                  {...register('notes')}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                  placeholder="Notes internes ou publiques..."
                />
              </div>
            </div>
          </Card>

          {/* Items */}
          {/* ─────────────────────────  Articles du Devis  ───────────────────────── */}
<Card className="p-4 space-y-4">
  <div className="flex items-center justify-between">
    <h3 className="text-lg font-semibold">Articles du Devis</h3>
    <Button
      type="button"
      onClick={() =>
        append({ itemType: 'service', description: '', quantity: 1, unitPrice: 0, taxRate: watch('taxRate') ?? 0 })
      }
    >
      <Plus className="w-4 h-4 mr-2" /> Ajouter une ligne
    </Button>
  </div>

  {loadingCatalog && (
    <div className="text-sm text-gray-500">Chargement du catalogue (services / produits / kits)…</div>
  )}

  <div className="space-y-3">
    {fields.map((field, index) => {
      const it = watch(`items.${index}`);
      const currentType: ItemType = (it?.itemType as ItemType) || 'service';

      const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const t = e.target.value as ItemType;
        // Reset minimal sur changement de type
        update(index, {
          itemType: t === 'kit' ? 'service' : t, // interne: 'kit' affiché côté UI seulement
          description: '',
          quantity: 1,
          unitPrice: 0,
          productId: undefined,
          serviceTypeId: undefined,
          taxRate: watch('taxRate') ?? 0,
        });
      };

      const handleArticleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        if (!id) return;

        if (currentType === 'service') {
          const svc = services.find(s => s._id === id);
          if (svc) onSelectService(index, svc);
        } else if (currentType === 'product') {
          const p = products.find(p => p._id === id);
          if (p) onSelectProduct(index, p);
        } else if (currentType === 'kit') {
          const k = kits.find(k => k._id === id);
          if (k) expandKitIntoItems(index, k);
        }
      };

      return (
        <div key={field.id} className="grid grid-cols-12 gap-3 items-start">
          {/* Type d'article */}
          <div className="col-span-12 md:col-span-2">
            <label htmlFor={`item-type-${index}`} className="block text-sm font-medium mb-1">Type d’article</label>
            <select
              id={`item-type-${index}`}
              name={`items[${index}].type`}
              value={currentType}
              onChange={handleTypeChange}
              className="w-full rounded border px-3 py-2"
            >
              <option value="service">Service</option>
              <option value="product">Produit</option>
              <option value="kit">Kit</option>
            </select>
          </div>

          {/* Article (nom + prix) */}
          <div className="col-span-12 md:col-span-4">
            <label htmlFor={`item-article-${index}`} className="block text-sm font-medium mb-1">
              {currentType === 'service' ? 'Type de service' : currentType === 'product' ? 'Produit' : 'Kit'}
            </label>
            <select
              id={`item-article-${index}`}
              name={`items[${index}].article`}
              onChange={handleArticleChange}
              className="w-full rounded border px-3 py-2"
              value={
                currentType === 'service'
                  ? it?.serviceTypeId || ''
                  : currentType === 'product'
                  ? it?.productId || ''
                  : ''
              }
            >
              <option value="">— Sélectionner —</option>
              {currentType === 'service' &&
                services.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.name} — {formatPrice(svcPrice(s))}
                  </option>
                ))}
              {currentType === 'product' &&
                products.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.name} — {formatPrice(prodPrice(p))}
                  </option>
                ))}
              {currentType === 'kit' &&
                kits.map(k => (
                  <option key={k._id} value={k._id}>
                    {k.name} {k.price ? `— ${formatPrice(k.price)}` : ''}
                  </option>
                ))}
            </select>
            {currentType === 'kit' && (
              <p className="mt-1 text-xs text-gray-500">
                La sélection d’un kit va ajouter ses produits en lignes séparées.
              </p>
            )}
          </div>

          {/* Description (pré-remplie, éditable) */}
          <div className="col-span-12 md:col-span-3">
            <label htmlFor={`item-description-${index}`} className="block text-sm font-medium mb-1">Description</label>
            <input
              id={`item-description-${index}`}
              className="w-full rounded border px-3 py-2"
              {...register(`items.${index}.description` as const)}
              placeholder="Description de l’article"
            />
          </div>

          {/* Qte */}
          <div className="col-span-6 md:col-span-1">
            <label htmlFor={`item-quantity-${index}`} className="block text-sm font-medium mb-1">Qté</label>
            <input
              id={`item-quantity-${index}`}
              type="number"
              min={1}
              className="w-full rounded border px-3 py-2"
              {...register(`items.${index}.quantity` as const, { valueAsNumber: true, min: 1 })}
            />
          </div>

          {/* PU */}
          <div className="col-span-6 md:col-span-1">
            <label htmlFor={`item-unitPrice-${index}`} className="block text-sm font-medium mb-1">PU</label>
            <input
              id={`item-unitPrice-${index}`}
              type="number"
              step="0.01"
              min={0}
              className="w-full rounded border px-3 py-2"
              {...register(`items.${index}.unitPrice` as const, { valueAsNumber: true, min: 0 })}
            />
          </div>

          {/* Supprimer */}
          <div className="col-span-12 md:col-span-1 flex md:items-end">
            <Button type="button" variant="ghost" onClick={() => remove(index)} className="text-red-600">
              <XCircle className="w-5 h-5 mr-1" /> Supprimer
            </Button>
          </div>

          {/* Champs cachés pour l’API */}
          <input type="hidden" {...register(`items.${index}.itemType` as const)} />
          <input type="hidden" {...register(`items.${index}.productId` as const)} />
          <input type="hidden" {...register(`items.${index}.serviceTypeId` as const)} />
          <input type="hidden" {...register(`items.${index}.taxRate` as const, { valueAsNumber: true })} />
        </div>
      );
    })}
  </div>
</Card>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/quotes')}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading} size="lg">
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? 'Mettre à jour' : 'Créer le Devis'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AddQuote;
